import { Link } from "react-router-dom";
import { Award, Check } from "lucide-react";
import Reveal, { SectionLabel } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { TRINITY, TRINITY_FAQ } from "../lib/site";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function Trinity() {
  return (
    <>
      <PageHero
        label="Trinity College London"
        title={<>An <em className="font-italic-accent italic gold-text">internationally recognised</em> pathway.</>}
        subtitle="BTSA prepares learners for Trinity College London Speech & Drama qualifications — a globally benchmarked credential that follows our students into universities and careers worldwide."
      />

      <section data-testid="trinity-intro" className="py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>What is Trinity?</SectionLabel>
              <h2 className="mt-6 font-serif text-3xl lg:text-4xl text-white font-medium uppercase tracking-wide leading-tight">
                145 years of recognised excellence
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <p className="font-sans text-base lg:text-lg text-white/65 leading-relaxed font-light">
                {TRINITY.intro}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-28 bg-[#080808] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>Why Trinity?</SectionLabel>
              <h3 className="mt-6 font-serif text-2xl lg:text-3xl text-white font-medium uppercase tracking-wide leading-tight">
                The advantage that lasts a lifetime
              </h3>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-5">
              {TRINITY.why.map((w, i) => (
                <Reveal key={w} delay={i * 0.05}>
                  <li className="flex items-start gap-4 border-b border-white/10 pb-5">
                    <Award size={18} className="text-[#d4af37] mt-1 shrink-0" />
                    <span className="font-sans text-base text-white/80 font-light">{w}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <div className="lux-card p-10 h-full">
              <SectionLabel>Grades Offered</SectionLabel>
              <div className="mt-6 grid grid-cols-3 gap-3">
                {TRINITY.grades.map((g) => (
                  <span key={g} className="px-3 py-3 hairline text-center font-serif text-sm gold-text uppercase">{g}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="lux-card p-10 h-full">
              <SectionLabel>Trinity Preparation at BTS</SectionLabel>
              <ol className="mt-6 space-y-4">
                {TRINITY.process.map((p, i) => (
                  <li key={p} className="flex items-start gap-4">
                    <span className="font-serif text-xl gold-text w-8 shrink-0">0{i + 1}</span>
                    <span className="font-sans text-sm text-white/75 font-light">{p}</span>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="trinity-faq" className="ivory-section py-24 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex justify-center">
              <SectionLabel>Frequently Asked</SectionLabel>
            </div>
            <h2 className="mt-7 font-serif text-3xl lg:text-4xl font-light text-center leading-tight">
              Trinity, <span className="gold-text italic">answered.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-12">
              {TRINITY_FAQ.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  data-testid={`trinity-faq-${i}`}
                  className="border-b border-black/10"
                >
                  <AccordionTrigger className="font-serif text-lg lg:text-xl font-light py-6 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-sans text-base text-[#5C5345] leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-[#080808] border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl lg:text-4xl text-white font-medium uppercase tracking-wide leading-tight">
              Begin the Trinity journey
            </h2>
            <p className="mt-5 font-sans text-base text-white/65 font-light">
              Our team will assess, recommend the right grade and design a preparation pathway tailored to your child.
            </p>
            <div className="mt-9">
              <Link to="/join" className="btn-gold font-sans text-[11px] tracking-[0.3em] uppercase px-9 py-5 font-semibold inline-block">Enquire Now</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
